import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoPicker } from "./PhotoPicker";
import { QuoteSuccess } from "./QuoteSuccess";
import { SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPTIONS = [...SERVICES.map((s) => s.formValue), "Multiple services / Not sure"];
const EMPTY = { name: "", phone: "", email: "", postcode: "", description: "", preferred_date: "", website: "" };

const Field = ({ id, label, children, hint }) => (
  <div className="space-y-2 min-w-0">
    <Label htmlFor={id} className="text-sm font-semibold text-[var(--navy)]">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const inputCls = "h-12 rounded-xl bg-white border-slate-300 text-base focus-visible:ring-[var(--green)]";

export const QuoteForm = () => {
  const [params] = useSearchParams();
  const initialService = SERVICES.find((s) => s.slug === params.get("service"))?.formValue || "";
  const [form, setForm] = useState(EMPTY);
  const [service, setService] = useState(initialService);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!service) { setError("Please choose a service category."); return; }
    setLoading(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
    fd.append("service", service);
    files.forEach((f) => fd.append("photos", f));
    try {
      const res = await axios.post(`${API}/quotes`, fd);
      setDone({ reference: res.data?.reference ?? "", name: form.name.split(" ")[0], confirmationTo: res.data?.confirmation_sent ? form.email : "" });
      setForm(EMPTY); setFiles([]);
    } catch (err) {
      const msg = err.response?.data?.detail;
      const text = typeof msg === "string" ? msg : "Sorry, something went wrong. Please call or WhatsApp us on 07836 207134.";
      setError(text);
      toast.error(text);
    } finally {
      setLoading(false);
    }
  };

  if (done) return <QuoteSuccess {...done} onReset={() => setDone(null)} />;

  return (
    <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2 [&>*]:min-w-0" data-testid="quote-form" noValidate={false}>
      <input type="text" name="website" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <Field id="q-name" label="Your name *">
        <Input id="q-name" required minLength={2} value={form.name} onChange={set("name")} autoComplete="name" className={inputCls} data-testid="quote-form-name-input" />
      </Field>
      <Field id="q-phone" label="Phone number *">
        <Input id="q-phone" type="tel" required value={form.phone} onChange={set("phone")} autoComplete="tel" className={inputCls} data-testid="quote-form-phone-input" />
      </Field>
      <Field id="q-email" label="Email address *">
        <Input id="q-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" className={inputCls} data-testid="quote-form-email-input" />
      </Field>
      <Field id="q-postcode" label="Property postcode *" hint="e.g. OX1 2AB">
        <Input id="q-postcode" required value={form.postcode} onChange={set("postcode")} autoComplete="postal-code" className={`${inputCls} uppercase`} data-testid="quote-form-postcode-input" />
      </Field>
      <Field id="q-service" label="Service category *">
        <Select value={service} onValueChange={setService}>
          <SelectTrigger id="q-service" className={inputCls} data-testid="quote-form-service-category-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
          <SelectContent>
            {OPTIONS.map((o, i) => <SelectItem key={o} value={o} data-testid={`quote-form-service-option-${i}`}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field id="q-date" label="Preferred appointment date" hint="Optional – we'll confirm availability with you">
        <Input id="q-date" type="date" min={today} value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="quote-form-date-input" />
      </Field>
      <div className="sm:col-span-2">
        <Field id="q-desc" label="Describe the work you need *">
          <Textarea id="q-desc" required minLength={10} rows={5} value={form.description} onChange={set("description")}
            placeholder="e.g. Paint the living room walls and skirting boards, roughly 4m x 5m…" className="rounded-xl bg-white border-slate-300 text-base focus-visible:ring-[var(--green)]" data-testid="quote-form-description-textarea" />
        </Field>
      </div>
      <div className="sm:col-span-2"><PhotoPicker files={files} setFiles={setFiles} /></div>
      {error && <p className="sm:col-span-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="quote-form-error">{error}</p>}
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
        <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={loading} data-testid="quote-form-submit-button"
          className="inline-flex items-center justify-center gap-3 rounded-full bg-[var(--green)] px-9 py-4 font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-[var(--green-hover)] disabled:opacity-70">
          {loading && <Loader2 className="h-5 w-5 animate-spin" />} {loading ? "SENDING…" : "REQUEST MY FREE QUOTE"}
        </motion.button>
        <p className="text-xs text-slate-500">By sending this form you agree to our <a href="/privacy" className="underline">Privacy Policy</a>.</p>
      </div>
    </form>
  );
};
